import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

/**
 * SummaryTab — SUMMARY tab
 *
 * BROKA      = brijeshComm (editable rate per CBM, user inputs this)
 * BROKARAGE  = brijeshComm × netCbm (auto-calculated amount per row, read-only)
 * BROKARAGE total (below table) = Σ(brijeshComm × netCbm) across all rows
 *
 * All calculations use useWatch({ control }) so SummaryTab re-renders
 * on every single keystroke — immediate, zero lag.
 */


const SummaryTab = ({
  control,
  watch,
  fields,
  append,
  remove,
  setValue,
  dataEntryId,
}) => {
  // ── Role check ──
  const userRole = sessionStorage.getItem('userRole') ?? '';
  const isLimitedAdmin = userRole.trim().toLowerCase() === 'limited admin';

  // useWatch creates a proper subscription inside THIS component
  // (parent's watch() function does NOT trigger SummaryTab re-renders — this does)
  const dataEntryForm    = useWatch({ control, name: 'dataEntryForm' });
  const roundOff         = useWatch({ control, name: 'roundOff' });
  const invoiceQuantity  = useWatch({ control, name: 'invoiceQuantity' });
  const invoiceRate      = useWatch({ control, name: 'invoiceRate' });
  const roundOffInvAmount = useWatch({ control, name: 'roundOffInvAmount' });
  const bgAmtInterestAmt = useWatch({ control, name: 'bgAmtInterestAmt' });

  // ── All calculations derived synchronously — immediate update on every keystroke ──
  const calc = useMemo(() => {
    const rows = Array.isArray(dataEntryForm) ? dataEntryForm : [];

    // Per-row computed values
    const rowAmts = rows.map((r) => (Number(r?.netCbm) || 0) * (Number(r?.avgRate) || 0));
    const rowBrok = rows.map((r) => (Number(r?.brijeshComm) || 0) * (Number(r?.netCbm) || 0));

    // Column totals
    const totalPcs      = rows.reduce((t, r) => t + (parseFloat(r?.pcs)      || 0), 0);
    const totalPlCbm    = rows.reduce((t, r) => t + (parseFloat(r?.plCbm)    || 0), 0);
    const totalGrossCbm = rows.reduce((t, r) => t + (parseFloat(r?.grossCbm) || 0), 0);
    const totalNetCbm   = rows.reduce((t, r) => t + (parseFloat(r?.netCbm)   || 0), 0);
    const totalsellCbm  = rows.reduce((t, r) => t + (parseFloat(r?.sellCbm)  || 0), 0);
    const totalAmt      = rowAmts.reduce((t, v) => t + v, 0);
    const totalBrijesh  = rowBrok.reduce((t, v) => t + v, 0);
    const totalAvgRate  = totalNetCbm ? totalAmt / totalNetCbm : 0;

    // Total Invoice Amount coming from INV DETAILS ((invoiceQuantity × invoiceRate) + roundOffInvAmount or bgAmtInterestAmt)
    const q = Number(invoiceQuantity) || 0;
    const r = Number(invoiceRate) || 0;
    const ro = Number(roundOffInvAmount) || 0;
    const invDetailsAmt = ((q * r) + ro) || Number(bgAmtInterestAmt) || 0;

    const totalInvoiceAmt = invDetailsAmt || (totalAmt - (Number(roundOff) || 0));
    const computedDiscount = invDetailsAmt ? (totalAmt - invDetailsAmt) : (Number(roundOff) || 0);

    return {
      rowAmts, rowBrok,
      totalPcs, totalPlCbm, totalGrossCbm, totalNetCbm, totalsellCbm,
      totalAmt, totalBrijesh, totalAvgRate, totalInvoiceAmt, computedDiscount,
    };
  }, [dataEntryForm, roundOff, invoiceQuantity, invoiceRate, roundOffInvAmount, bgAmtInterestAmt]);

  // ── Sync totals to form store (needed by DataEntryForm shipping-charges calc & INV DETAILS) ──
  useEffect(() => {
    setValue('totalNetCbm',     Number(calc.totalNetCbm).toFixed(2));
    setValue('totalAmount',     calc.totalAmt);
    setValue('totalAvgRate',    Number(calc.totalAvgRate).toFixed(2));
    setValue('totalPcs',        calc.totalPcs);
    setValue('totalPlCbm',      calc.totalPlCbm);
    setValue('totalGrossCbm',   calc.totalGrossCbm);
    setValue('totalsellCbm',    Number(calc.totalsellCbm).toFixed(2));
    setValue('brijesh',         Number(calc.totalBrijesh).toFixed(2));
    setValue('totalInvoiceAmt', calc.totalInvoiceAmt);
    setValue('bgAmtInterestAmt', calc.totalInvoiceAmt);
    if (calc.computedDiscount !== undefined && !isNaN(calc.computedDiscount)) {
      setValue('roundOff', Number(calc.computedDiscount).toFixed(2));
    }
    // Auto-fill INV DETAILS (matches existing software behaviour)
    if (!invoiceQuantity) setValue('invoiceQuantity', Number(calc.totalNetCbm).toFixed(3));
    if (!invoiceRate) setValue('invoiceRate', Number(calc.totalAvgRate).toFixed(2));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc]);

  const [openDrawerIndex, setOpenDrawerIndex] = useState(null);

  const addRow = () => {
    append({
      containerNo:  '',
      averageLength:'',
      avgCftAvgGirth:'',
      pcs:          '',
      plCbm:        '',
      grossCbm:     '',
      netCbm:       '',
      avgRate:      '',
      amt:          '',
      lorry:        '',
      sellRate:     '',
      sellCbm:      '',
      brijeshComm:  '',
      agent:        '',
      pkl:          '',
      status:       'Active',
      containerImage: [],
    });
  };

  return (
    <div className="de-tab-content de-tab-content--wide">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="de-section-title mb-0">SUMMARY</h5>
        <button type="button" className="btn btn-sm btn-orange" onClick={addRow}>
          + Add Row
        </button>
      </div>

      {/* ── Scrollable Table ── */}
      <div className="de-summary-table-wrapper">
        <table className="table table-bordered table-hover align-middle de-summary-table text-nowrap">
          <thead className="gtss-bg-primary text-white text-uppercase">
            <tr>
              <th>#</th>
              <th>CONTAINER NO</th>
              <th>AVG LENTH</th>
              <th>AVG CFT/AVG GIRTH</th>
              <th>PCS</th>
              <th>BL CBM</th>
              <th>GROSS CBM</th>
              <th>NET CBM</th>
              <th>RATE</th>
              <th>AMT</th>
              {!isLimitedAdmin && <th>LORRY</th>}
              {!isLimitedAdmin && <th>SELL RATE</th>}
              {!isLimitedAdmin && <th>SELL CBM</th>}
              <th>BROKA</th>
              <th>BROKARAGE</th>
              {!isLimitedAdmin && <th>AGENT NAME</th>}
              {!isLimitedAdmin && <th>PKL</th>}
              <th>STATUS</th>
              <th>DEL</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => (
              <tr key={field.id}>
                <td className="text-center text-muted">{index + 1}</td>

                {/* CONTAINER NO */}
                <td style={{ minWidth: 140 }}>
                  <Controller
                    name={`dataEntryForm.${index}.containerNo`}
                    control={control}
                    render={({ field: f }) => (
                      <input {...f} type="text" className="form-control form-control-sm" />
                    )}
                  />
                </td>

                {/* AVG LENTH */}
                <td style={{ minWidth: 100 }}>
                  <Controller
                    name={`dataEntryForm.${index}.averageLength`}
                    control={control}
                    render={({ field: f }) => (
                      <input {...f} type="number" className="form-control form-control-sm" />
                    )}
                  />
                </td>

                {/* AVG CFT/AVG GIRTH */}
                <td style={{ minWidth: 110 }}>
                  <Controller
                    name={`dataEntryForm.${index}.avgCftAvgGirth`}
                    control={control}
                    render={({ field: f }) => (
                      <input {...f} type="number" className="form-control form-control-sm" />
                    )}
                  />
                </td>

                {/* PCS */}
                <td style={{ minWidth: 80 }}>
                  <Controller
                    name={`dataEntryForm.${index}.pcs`}
                    control={control}
                    render={({ field: f }) => (
                      <input {...f} type="number" className="form-control form-control-sm" />
                    )}
                  />
                </td>

                {/* BL CBM */}
                <td style={{ minWidth: 90 }}>
                  <Controller
                    name={`dataEntryForm.${index}.plCbm`}
                    control={control}
                    render={({ field: f }) => (
                      <input {...f} type="number" className="form-control form-control-sm" />
                    )}
                  />
                </td>

                {/* GROSS CBM */}
                <td style={{ minWidth: 100 }}>
                  <Controller
                    name={`dataEntryForm.${index}.grossCbm`}
                    control={control}
                    render={({ field: f }) => (
                      <input {...f} type="number" className="form-control form-control-sm" />
                    )}
                  />
                </td>

                {/* NET CBM */}
                <td style={{ minWidth: 90 }}>
                  <Controller
                    name={`dataEntryForm.${index}.netCbm`}
                    control={control}
                    render={({ field: f }) => (
                      <input {...f} type="number" className="form-control form-control-sm" />
                    )}
                  />
                </td>

                {/* RATE (avgRate) */}
                <td style={{ minWidth: 90 }}>
                  <Controller
                    name={`dataEntryForm.${index}.avgRate`}
                    control={control}
                    render={({ field: f }) => (
                      <input {...f} type="number" className="form-control form-control-sm" />
                    )}
                  />
                </td>

                {/* AMT — netCbm × avgRate, computed inline (immediate, no setValue lag) */}
                <td style={{ minWidth: 100 }}>
                  <input
                    type="number"
                    className="form-control form-control-sm de-readonly-field"
                    value={calc.rowAmts[index] || ''}
                    readOnly
                    tabIndex={-1}
                  />
                </td>

                {/* LORRY */}
                {!isLimitedAdmin && (
                  <td style={{ minWidth: 110 }}>
                    <Controller
                      name={`dataEntryForm.${index}.lorry`}
                      control={control}
                      render={({ field: f }) => (
                        <input {...f} type="text" className="form-control form-control-sm" />
                      )}
                    />
                  </td>
                )}

                {/* SELL RATE */}
                {!isLimitedAdmin && (
                  <td style={{ minWidth: 90 }}>
                    <Controller
                      name={`dataEntryForm.${index}.sellRate`}
                      control={control}
                      render={({ field: f }) => (
                        <input {...f} type="number" className="form-control form-control-sm" />
                      )}
                    />
                  </td>
                )}

                {/* SELL CBM */}
                {!isLimitedAdmin && (
                  <td style={{ minWidth: 90 }}>
                    <Controller
                      name={`dataEntryForm.${index}.sellCbm`}
                      control={control}
                      render={({ field: f }) => (
                        <input {...f} type="number" className="form-control form-control-sm" />
                      )}
                    />
                  </td>
                )}

                {/* BROKA (brijeshComm — per-row rate) */}
                <td style={{ minWidth: 90 }}>
                  <Controller
                    name={`dataEntryForm.${index}.brijeshComm`}
                    control={control}
                    render={({ field: f }) => (
                      <input {...f} type="number" className="form-control form-control-sm" />
                    )}
                  />
                </td>

                {/* BROKARAGE — brijeshComm × netCbm, computed inline */}
                <td style={{ minWidth: 100 }}>
                  <input
                    type="number"
                    className="form-control form-control-sm de-readonly-field"
                    value={calc.rowBrok[index] != null ? Number(calc.rowBrok[index]).toFixed(2) : ''}
                    readOnly
                    tabIndex={-1}
                  />
                </td>

                {/* AGENT NAME */}
                {!isLimitedAdmin && (
                  <td style={{ minWidth: 110 }}>
                    <Controller
                      name={`dataEntryForm.${index}.agent`}
                      control={control}
                      render={({ field: f }) => (
                        <input {...f} type="text" className="form-control form-control-sm" placeholder="Agent Name" />
                      )}
                    />
                  </td>
                )}

                {/* PKL */}
                {!isLimitedAdmin && (
                  <td style={{ minWidth: 80 }}>
                    <Controller
                      name={`dataEntryForm.${index}.pkl`}
                      control={control}
                      render={({ field: f }) => (
                        <input {...f} type="number" className="form-control form-control-sm" />
                      )}
                    />
                  </td>
                )}

                {/* STATUS — dropdown */}
                <td style={{ minWidth: 110 }}>
                  <Controller
                    name={`dataEntryForm.${index}.status`}
                    control={control}
                    render={({ field: f }) => (
                      <select {...f} className="form-select form-select-sm">
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    )}
                  />
                </td>

                {/* DELETE ROW */}
                <td className="text-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => {
                      remove(index);
                      if (openDrawerIndex === index) setOpenDrawerIndex(null);
                    }}
                    disabled={fields.length === 1}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>

          {/* ── Table Totals Footer ── */}
          <tfoot className="table-secondary fw-bold">
            <tr>
              <td colSpan={4} className="text-end text-uppercase small">Totals →</td>
              <td>{calc.totalPcs || 0}</td>
              <td>{calc.totalPlCbm || 0}</td>
              <td>{calc.totalGrossCbm || 0}</td>
              <td>{Number(calc.totalNetCbm).toFixed(2)}</td>
              <td>{Number(calc.totalAvgRate).toFixed(2)}</td>
              <td>{Number(calc.totalAmt).toFixed(2)}</td>
              {/* LORRY + SELL RATE + SELL CBM colspan — hidden for Limited Admin */}
              {!isLimitedAdmin && <td colSpan={3}>{Number(calc.totalsellCbm).toFixed(2)}</td>}
              <td></td>
              <td>{Number(calc.totalBrijesh).toFixed(2)}</td>
              {/* AGENT NAME + PKL colspan — hidden for Limited Admin */}
              {isLimitedAdmin ? <td colSpan={2}></td> : <td colSpan={5}></td>}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ── Invoice Totals ── */}
      <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
        {/* Row 1 — TOTAL AMOUNT */}
        <div className="d-flex align-items-center gap-2">
          <label style={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: '10.5px', color: '#374151', textTransform: 'uppercase' }}>TOTAL AMOUNT :</label>
          <input
            type="number"
            className="form-control de-readonly-field de-calc-field"
            style={{ width: 130 }}
            value={calc.totalAmt !== 0 ? Number(calc.totalAmt).toFixed(2) : ''}
            readOnly
            tabIndex={-1}
          />
        </div>

        <div className="d-flex align-items-center gap-2">
          <label style={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: '10.5px', color: '#374151', textTransform: 'uppercase' }}>DISCOUNT :</label>
          <Controller
            name="roundOff"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="number"
                className="form-control"
                style={{ width: 130 }}
                placeholder="0"
              />
            )}
          />
        </div>

        <div className="d-flex align-items-center gap-2">
          <label style={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: '10.5px', color: '#374151', textTransform: 'uppercase' }}>TOTAL INVOICE AMOUNT :</label>
          <input
            type="number"
            className="form-control de-readonly-field de-calc-field"
            style={{ width: 130 }}
            value={Number(calc.totalInvoiceAmt).toFixed(2)}
            readOnly
            tabIndex={-1}
          />
        </div>

        {/* Force BROKARAGE to next row */}
        <div className="w-100" />

        {/* Row 2 — BROKARAGE & PAID AND UNPAID */}
        <div className="d-flex align-items-center gap-2">
          <label style={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: '10.5px', color: '#374151', textTransform: 'uppercase' }}>BROKARAGE :</label>
          <input
            type="number"
            className="form-control de-readonly-field de-calc-field"
            style={{ width: 130 }}
            value={Number(calc.totalBrijesh).toFixed(2)}
            readOnly
            tabIndex={-1}
          />
        </div>

        <div className="d-flex align-items-center gap-2 ms-3">
          <Controller
            name="paidandunpaid"
            control={control}
            render={({ field: { value, onChange } }) => (
              <div className="form-check mb-0 d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  className="form-check-input mt-0"
                  id="paidandunpaidCheck"
                  checked={!!value}
                  onChange={(e) => onChange(e.target.checked)}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <label
                  className="form-check-label text-dark mb-0 ms-1"
                  htmlFor="paidandunpaidCheck"
                  style={{ cursor: 'pointer', fontWeight: 600, fontSize: '10.5px', color: '#374151', textTransform: 'uppercase' }}
                >
                  MARK AS PAID
                </label>
              </div>
            )}
          />
        </div>
      </div>

    </div>
  );
};

export { SummaryTab };
export default SummaryTab;
